/**
 * OKS-1006 D3 — after a refused submit, the reason must be IN VIEW and
 * FOCUSED, never behind a closed disclosure or below the fold at 402px.
 *
 * Why this exists: once a field refusal stopped firing a toast (BRIEF-OKS-996
 * §5 channel rule, "never both"), an inline message the operator cannot see is
 * a SILENT refusal — worse than the toast it replaced ("never neither").
 */

/**
 * Marks the element to focus when a refusal carries NO field — the dialog-level
 * `errors.base` message. Field errors always win (see {@link focusFirstRefusal}).
 */
export const REFUSAL_FOCUS_ATTR = "data-refusal-focus"

/**
 * Focus (and centre) the first refused control inside `container`, NOW.
 *
 * ⚠️ SYNCHRONOUS, AND IT MUST BE CALLED AFTER THE ERRORED RENDER HAS COMMITTED
 * — use {@link useFocusOnRefusal}, never an Inertia `onError` callback.
 * `@inertiajs/react`'s `useForm` calls the caller's `onError` synchronously
 * right after `setErrors`, i.e. BEFORE React commits the render that mounts
 * the `aria-invalid` control, opens the disclosure around it, or mounts the
 * dialog-level message. OKS-1006 W2 shipped this behind one
 * `requestAnimationFrame` from `onError`; the frame lost the race to the
 * commit ~40% of runs (LQE finding 88: E6 4/10, E1 2/6), found no target,
 * returned silently, and focus fell to the page body — out of the modal —
 * because the confirm button had gone `disabled` while processing. jsdom
 * commits synchronously, so no component test could see it.
 *
 * - Picks the first `[aria-invalid="true"]` in DOM ORDER, so it follows
 *   whichever layout is mounted (queue: link → number → date; panel: number →
 *   link → date; the variant control above both) without a hand-kept order.
 * - Falls back to the element carrying {@link REFUSAL_FOCUS_ATTR} (the
 *   dialog-level message) when no control is invalid.
 * - `focus({ preventScroll: true })` + `scrollIntoView({ block: "center" })`:
 *   the message sits UNDER its control, and a plain `focus()` scrolls only the
 *   control into view. `scrollIntoView` is optional-called (jsdom lacks it).
 *
 * @returns whether a target was found and focused.
 */
export function focusFirstRefusal(container: HTMLElement | null): boolean {
  if (!container) return false
  const target =
    container.querySelector<HTMLElement>('[aria-invalid="true"]') ??
    container.querySelector<HTMLElement>(`[${REFUSAL_FOCUS_ATTR}]`)
  if (!target) return false

  target.focus({ preventScroll: true })
  target.scrollIntoView?.({ block: "center" })
  return true
}

/**
 * `aria-describedby` from the ids that currently exist. Falsy entries are
 * dropped, so an error id is referenced ONLY while its message renders — a
 * describedby pointing at an unmounted id is announced as nothing.
 */
export function describedBy(
  ...ids: (string | false | null | undefined)[]
): string | undefined {
  const present = ids.filter((id): id is string => Boolean(id))
  return present.length > 0 ? present.join(" ") : undefined
}
